import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { Badge } from '@/components/ui/badge'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Loader2, Square } from 'lucide-react'
import type { ProjectTask, Message } from '@/types/conversations'

interface TaskBadgeProps {
  task: ProjectTask
  messages: Message[]
  currentActivity?: string | null
  activityHistory?: string[]
  onStop: (taskId: number) => void
}

export function TaskBadge({ task, messages, currentActivity, activityHistory = [], onStop }: TaskBadgeProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isStopping, setIsStopping] = useState(false)

  const todos = task.todos || []
  const totalTodos = todos.length
  const completedTodos = todos.filter(t => t.status === 'completed').length
  const progressText = totalTodos > 0 ? `${completedTodos}/${totalTodos}` : ''

  // Get task-related assistant messages (hidden from chat)
  const taskMessages = messages.filter(m =>
    m.task_id === task.id &&
    m.role === 'assistant' &&
    m.content
  )
  const latestMessage = taskMessages[taskMessages.length - 1]

  return (
    <div className="mx-2 mb-1">
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <div className="w-full flex items-center gap-2 px-3 py-2 rounded-xl border border-border/50 backdrop-blur-xs bg-background/60 hover:bg-background/80 transition-colors cursor-pointer text-left">
            <span className="relative flex h-2.5 w-2.5 flex-shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary" />
            </span>
            <span className="text-xs font-medium text-foreground truncate flex-1">
              {task.title}
            </span>
            {task.target === 'main' ? (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 flex-shrink-0">
                Main
              </Badge>
            ) : task.coding_worker_name ? (
              <Badge variant="outline" className="text-[9px] px-1.5 py-0 h-4 capitalize flex-shrink-0">
                {task.coding_worker_name}
              </Badge>
            ) : null}
            {progressText && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 flex-shrink-0">
                {progressText}
              </Badge>
            )}
            <button
              className="h-5 w-5 rounded flex items-center justify-center text-muted-foreground/60 hover:text-red-500 hover:bg-red-500/10 flex-shrink-0 transition-colors disabled:opacity-50"
              disabled={isStopping}
              onClick={async (e) => {
                e.stopPropagation()
                setIsStopping(true)
                await onStop(task.id)
                setIsStopping(false)
              }}
              title="Stop building"
            >
              {isStopping ? <Loader2 className="h-2.5 w-2.5 animate-spin" /> : <Square className="h-2.5 w-2.5 fill-current" />}
            </button>
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 max-h-[300px] overflow-hidden backdrop-blur-lg bg-background/80 border-border/50"
          side="top"
          align="start"
          sideOffset={4}
        >
          <div className="flex flex-col max-h-[300px]">
            {/* Streaming message content — shown first */}
            <div className="overflow-y-auto px-3 pt-3 pb-2">
              {latestMessage ? (
                <div className="text-[12px] text-foreground/80 leading-relaxed shimmer-text prose prose-xs dark:prose-invert max-w-none [&_p]:m-0 [&_p]:mb-1 [&_code]:text-[10px] [&_pre]:text-[10px] [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0">
                  <ReactMarkdown>{latestMessage.content?.slice(0, 800) || ''}</ReactMarkdown>
                  {(latestMessage.content?.length ?? 0) > 800 && (
                    <span className="text-muted-foreground">...</span>
                  )}
                </div>
              ) : (
                <div className="text-[12px] text-muted-foreground/60 italic shimmer-text">
                  Building...
                </div>
              )}
            </div>

            {/* Activity / Tool use — shown below content */}
            {(currentActivity || activityHistory.length > 0) && (
              <div className="px-3 pb-2.5 pt-1 border-t border-border/30">
                <div className="space-y-0.5">
                  {activityHistory.slice(-3).map((activity, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[10px] text-muted-foreground/40 line-through">
                      <span className="h-1 w-1 rounded-full bg-muted-foreground/20 flex-shrink-0" />
                      <span className="truncate">{activity}</span>
                    </div>
                  ))}
                  {currentActivity && (
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse flex-shrink-0" />
                      <span className="truncate">{currentActivity}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
